import { ref, computed } from 'vue';
import { positionApi, type Position } from '@/entities/Position';
import { useDepartmentOptions } from './useDepartmentOptions';
import { useFunctionalActivityOptions } from './useFunctionalActivityOptions';
import { fetchAllPages } from '@/shared/lib';

export interface DeptPositionPair {
  deptId: number | null;
  positionId: number | null;
}

export interface FunctionalActivitySlot {
  id: number | null;
}

export function useMemberPositionForm() {
  const deptPositionPairs = ref<DeptPositionPair[]>([{ deptId: null, positionId: null }]);
  const positionsByDept = ref<Record<number, Position[]>>({});
  const isLoadingPositions = ref<Record<number, boolean>>({});
  const functionalActivitySlots = ref<FunctionalActivitySlot[]>([{ id: null }]);
  const hasFunctionalActivity = ref(false);
  const savedFunctionalActivitySlots = ref<FunctionalActivitySlot[]>([]);

  const {
    departments,
    loadDepartments,
  } = useDepartmentOptions();

  const {
    functionalActivities,
    loadFunctionalActivities,
  } = useFunctionalActivityOptions();

  const completePairs = computed(() =>
    deptPositionPairs.value.filter((p) => p.deptId !== null && p.positionId !== null)
  );

  const usedPositionIds = computed(() =>
    completePairs.value.map((p) => p.positionId as number)
  );

  const availableFunctionalActivities = computed(() => {
    const selectedIds = new Set(
      functionalActivitySlots.value
        .map((s) => s.id)
        .filter((id): id is number => id !== null)
    );
    return functionalActivities.value.filter((fa) => !selectedIds.has(fa.id));
  });

  const canAddFunctionalActivity = computed(() => {
    const last = functionalActivitySlots.value[functionalActivitySlots.value.length - 1];
    if (!last || last.id === null) return false;
    return (
      functionalActivities.value.length > 0 &&
      functionalActivitySlots.value.length < functionalActivities.value.length
    );
  });

  const canAddPair = computed(() => {
    const lastPair = deptPositionPairs.value[deptPositionPairs.value.length - 1];
    if (!lastPair || lastPair.deptId === null || lastPair.positionId === null) return false;

    if (departments.value.length === 0) return false;

    const allDeptsLoaded = departments.value.every((d) => positionsByDept.value[d.id] !== undefined);
    if (allDeptsLoaded) {
      const totalCombos = departments.value.reduce(
        (sum, dept) => sum + (positionsByDept.value[dept.id]?.length ?? 0),
        0
      );
      if (deptPositionPairs.value.length >= totalCombos) return false;
    }

    const usedDeptPositionCombos = new Set(
      completePairs.value.map((p) => `${p.deptId}-${p.positionId}`)
    );

    for (const dept of departments.value) {
      const positions = positionsByDept.value[dept.id];
      if (positions === undefined) return true;
      for (const pos of positions) {
        if (!usedDeptPositionCombos.has(`${dept.id}-${pos.id}`)) return true;
      }
    }
    return false;
  });

  function availablePositionsForPair(pairIndex: number): Position[] {
    const pair = deptPositionPairs.value[pairIndex];
    if (pair?.deptId === null) return [];
    const all = positionsByDept.value[pair.deptId] ?? [];
    const otherUsed = new Set(
      usedPositionIds.value.filter((id) => id !== pair.positionId)
    );
    return all.filter((p) => !otherUsed.has(p.id));
  }

  function getDeptOptions(pairIndex: number) {
    const otherPairs = deptPositionPairs.value.filter((_, i) => i !== pairIndex);
    const pairPositionIds = new Set(otherPairs.map((pp) => pp.positionId).filter(Boolean));
    const usedDeptIds = new Set(
      otherPairs
        .filter((p): p is DeptPositionPair & { deptId: number } => p.deptId !== null)
        .filter((p) => {
          const positions = positionsByDept.value[p.deptId] ?? [];
          return positions.length > 0 && positions.every((pos) => pairPositionIds.has(pos.id));
        })
        .map((p) => p.deptId)
    );

    return departments.value
      .filter((d) => !usedDeptIds.has(d.id))
      .map((d) => ({ id: d.id, name: d.code }));
  }

  function getPositionOptions(pairIndex: number) {
    return availablePositionsForPair(pairIndex).map((p) => ({ id: p.id, name: p.name }));
  }

  function getFunctionalActivityOptions(slotIndex: number) {
    const currentId = functionalActivitySlots.value[slotIndex]?.id;
    const available = availableFunctionalActivities.value.map((fa) => ({
      id: fa.id,
      name: fa.name,
    }));
    if (currentId !== null) {
      const current = functionalActivities.value.find((fa) => fa.id === currentId);
      if (current && !available.some((a) => a.id === current.id)) {
        return [{ id: current.id, name: current.name }, ...available];
      }
    }
    return available;
  }

  async function loadPositionsForDept(deptId: number) {
    if (positionsByDept.value[deptId]) return;
    isLoadingPositions.value = { ...isLoadingPositions.value, [deptId]: true };
     try {
      const result = await fetchAllPages((page) =>
        positionApi.searchPositions({ departmentId: deptId, active: true, page })
      );
      positionsByDept.value = { ...positionsByDept.value, [deptId]: result };
    } catch {
      positionsByDept.value = { ...positionsByDept.value, [deptId]: [] };
    } finally {
      isLoadingPositions.value = { ...isLoadingPositions.value, [deptId]: false };
    }
  }

  async function setDeptForPair(index: number, deptId: number | null) {
    deptPositionPairs.value = deptPositionPairs.value.map((pair, i) =>
      i === index ? { deptId, positionId: null } : pair
    );
    if (deptId !== null) {
      await loadPositionsForDept(deptId);
    }
  }

  function setPositionForPair(index: number, positionId: number | null) {
    deptPositionPairs.value = deptPositionPairs.value.map((pair, i) =>
      i === index ? { ...pair, positionId } : pair
    );
  }

  function removePair(index: number) {
    if (deptPositionPairs.value.length === 1) {
      deptPositionPairs.value = [{ deptId: null, positionId: null }];
      return;
    }
    deptPositionPairs.value = deptPositionPairs.value.filter((_, i) => i !== index);
  }

  function addPair() {
    deptPositionPairs.value = [...deptPositionPairs.value, { deptId: null, positionId: null }];
  }

  function setFunctionalActivity(index: number, id: number | null) {
    functionalActivitySlots.value = functionalActivitySlots.value.map((slot, i) =>
      i === index ? { id } : slot
    );
  }

  function removeFunctionalActivity(index: number) {
    if (functionalActivitySlots.value.length === 1) {
      functionalActivitySlots.value = [{ id: null }];
      return;
    }
    functionalActivitySlots.value = functionalActivitySlots.value.filter((_, i) => i !== index);
  }

  function addFunctionalActivity() {
    functionalActivitySlots.value = [...functionalActivitySlots.value, { id: null }];
  }

  function toggleFunctionalActivity(value: boolean) {
    hasFunctionalActivity.value = value;
    if (value) {
      if (savedFunctionalActivitySlots.value.length > 0) {
        functionalActivitySlots.value = [...savedFunctionalActivitySlots.value];
        savedFunctionalActivitySlots.value = [];
      } else {
        functionalActivitySlots.value = [{ id: null }];
      }
    } else {
      savedFunctionalActivitySlots.value = functionalActivitySlots.value.filter((s) => s.id !== null);
      functionalActivitySlots.value = [{ id: null }];
    }
  }

  function resetForm() {
    deptPositionPairs.value = [{ deptId: null, positionId: null }];
    positionsByDept.value = {};
    isLoadingPositions.value = {};
    functionalActivitySlots.value = [{ id: null }];
    hasFunctionalActivity.value = false;
    savedFunctionalActivitySlots.value = [];
  }

  async function hydrateFromMember(
    pairs: { deptId: number; positionId: number }[],
    faIds: number[]
  ) {
    positionsByDept.value = {};
    isLoadingPositions.value = {};
    savedFunctionalActivitySlots.value = [];
    deptPositionPairs.value =
      pairs.length > 0 ? pairs : [{ deptId: null, positionId: null }];
    hasFunctionalActivity.value = faIds.length > 0;
    functionalActivitySlots.value = faIds.length > 0 ? faIds.map((id) => ({ id })) : [{ id: null }];
    await Promise.all(
      [...new Set(pairs.map((p) => p.deptId))].map((deptId) =>
        loadPositionsForDept(deptId)
      )
    );
  }

  return {
    deptPositionPairs,
    positionsByDept,
    isLoadingPositions,
    functionalActivitySlots,
    hasFunctionalActivity,
    departments,
    functionalActivities,
    completePairs,
    availableFunctionalActivities,
    canAddPair,
    canAddFunctionalActivity,
    getDeptOptions,
    getPositionOptions,
    getFunctionalActivityOptions,
    loadPositionsForDept,
    setDeptForPair,
    setPositionForPair,
    removePair,
    addPair,
    setFunctionalActivity,
    removeFunctionalActivity,
    addFunctionalActivity,
    toggleFunctionalActivity,
    loadDepartments,
    loadFunctionalActivities,
    resetForm,
    hydrateFromMember,
  };
}
